import React, { useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import SimpleBar from "simplebar-react";
import { Row, Col, Spinner, UncontrolledDropdown, DropdownToggle } from "reactstrap";
import { useCookies } from "react-cookie";
import moment from "moment";
import { Button, Icon, UserAvatar } from "../../../components/Component";
import { findUpper } from "../../../utils/Utils";
import { igGetMessages, igGetProfile, igSendMessage, igSendMediaMessage } from "../../../services/igAdapter";
import { serviceGetUsersByUserId } from "../../../services/users";
import IgChatBubble from "./IgChatBubble";
import { upsertIncomingMessage } from "./utils/igMessageUtils";
import IgChatSearchSidebar from "./IgChatSearchSidebar";
import IgChatModalAssign from "./IgChatModalAssign";
import IgChatModalResolve from "./IgChatModalResolve";
import avatarPlaceholder from "../../../images/default-avatar.webp";
import EmojiPicker from "emoji-picker-react";
import classNames from "classnames";
import { serviceGetQuickReplyChat } from "../../../services/quickReply";
import { useKeyPress } from "../../../utils/Keypress";

// Konversi tanggal ke label yang readable
function getDateLabel(date) {
  const m = moment(date);
  if (!m.isValid()) return null;
  const today = moment().startOf("day");
  const diff = today.diff(m.clone().startOf("day"), "days");
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return m.format("dddd");
  return m.format("D MMMM YYYY");
}

// Sisipkan item separator di antara messages yang berbeda hari
function buildItems(messages) {
  const items = [];
  let lastDateKey = null;
  for (const msg of messages) {
    const ts = msg.created_time || msg.created_at;
    const dateKey = ts ? moment(ts).format("YYYY-MM-DD") : null;
    if (dateKey && dateKey !== lastDateKey) {
      items.push({ type: "separator", key: "sep-" + dateKey, label: getDateLabel(ts) });
      lastDateKey = dateKey;
    }
    items.push({ type: "message", key: msg.message_id, msg });
  }
  return items;
}

const ChatDateSeparator = ({ label }) => (
  <div className="d-flex align-items-center my-3 px-3">
    <div style={{ flex: 1, height: 1, backgroundColor: "#e5e9f2" }} />
    <span className="mx-2 text-soft" style={{ fontSize: 11, whiteSpace: "nowrap" }}>
      {label}
    </span>
    <div style={{ flex: 1, height: 1, backgroundColor: "#e5e9f2" }} />
  </div>
);

const PAGE_SIZE = 50;

const QuickReplyItem = ({ command, content, clickEvent, mouseEnterEvent, mouseLeaveEvent, active }) => (
  <Row
    className={`py-1 quick-reply-item${active ? " quick-reply-item-active" : ""}`}
    onClick={clickEvent}
    onMouseEnter={mouseEnterEvent}
    onMouseLeave={mouseLeaveEvent}
    style={{ cursor: "pointer" }}
  >
    <Col md="3" lg="3" className="quick-reply-command pl-4">
      {command}
    </Col>
    <Col md="9" lg="9">
      {content}
    </Col>
  </Row>
);

/**
 * IgChatBody — area kanan halaman Instagram Platform.
 *
 * Props:
 *   chat: ChatResponse — conversation yang sedang aktif, atau null jika belum pilih
 *   newInboundMessage: object | null — pesan baru dari WebSocket (dari Instagram.js)
 *   newSystemMessage: object | null — system message baru dari WebSocket (assign/unassign)
 */
const IgChatBody = ({ chat, newInboundMessage, newSystemMessage }) => {
  const [cookies] = useCookies(["user"]);
  const [messages, setMessages] = useState([]);

  // Lookup for reply quotes: message_id → message. Undefined entries are fine —
  // the bubble falls back to a generic label when the original isn't loaded.
  const messagesById = useMemo(() => {
    const byId = {};
    for (const msg of messages) byId[msg.message_id] = msg;
    return byId;
  }, [messages]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [inputText, setInputText] = useState("");
  const [isSending, setIsSending] = useState(false);
  // Message being replied to (null = normal message) — same UX as WA ChatBody.
  const [replyMessage, setReplyMessage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [documentFile, setDocumentFile] = useState(null);
  const [modalImage, setModalImage] = useState(false);
  const [modalVideo, setModalVideo] = useState(false);
  const [modalDocument, setModalDocument] = useState(false);
  const inputChatMedia = useRef(null);
  const bodyChatRef = useRef(null);
  const [chatOptions, setChatOptions] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [isResolveOpen, setIsResolveOpen] = useState(false);
  const [searchSidebar, setSearchSidebar] = useState(false);
  const [scrollToMessageId, setScrollToMessageId] = useState(null);
  const [conversationStatus, setConversationStatus] = useState("OPENED");
  const [, setAssignedName] = useState(null);
  const [participantProfile, setParticipantProfile] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiButtonRef = useRef(null);
  const [showQuickReply, setShowQuickReply] = useState(false);
  const showQuickReplyRef = useRef(false);
  const [quickReplyData, setQuickReplyData] = useState([]);
  const [searchQuickReply, setSearchQuickReply] = useState("");
  const quickReplyDebounceRef = useRef(null);
  const [isQuickReplyLoading, setIsQuickReplyLoading] = useState(false);
  const [cursor, setCursor] = useState(0);
  const [hovered, setHovered] = useState(undefined);
  const downPress = useKeyPress("ArrowDown");
  const upPress = useKeyPress("ArrowUp");
  const enterPress = useKeyPress("Enter");
  const tabPress = useKeyPress("Tab");
  const roleFunctions = JSON.parse(localStorage.getItem("permission") || "{}")?.chats?.access || [];

  const bottomRef = useRef(null);

  // ─── File helpers ────────────────────────────────────────────────────────────

  const convertFileSize = (size) => {
    if (size === 0) return "0 Bytes";
    const k = 1000;
    const decimal = 2;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const index = Math.floor(Math.log(size) / Math.log(k));
    return parseFloat((size / Math.pow(k, index)).toFixed(decimal)) + " " + sizes[index];
  };

  const setImageInput = (file) => {
    setImageFile(file);
    if (file) { setChatOptions(false); setModalImage(true); setModalVideo(false); setModalDocument(false); }
  };
  const setVideoInput = (file) => {
    setVideoFile(file);
    if (file) { setChatOptions(false); setModalVideo(true); setModalImage(false); setModalDocument(false); }
  };

  const handleImageFileChange = (e) => {
    if (e.target.files) { setImageInput(e.target.files[0]); e.target.value = null; }
  };
  const handleVideoFileChange = (e) => {
    if (e.target.files) { setVideoInput(e.target.files[0]); e.target.value = null; }
  };

  const handleUploadImageClick = () => {
    document.getElementById("ig-select-image").click();
    if (imageFile) setImageFile(null);
  };
  const handleUploadVideoClick = () => {
    document.getElementById("ig-select-video").click();
    if (videoFile) setVideoFile(null);
  };

  const onChatOptions = () => setChatOptions(!chatOptions);

  const closePreviewModal = () => {
    setModalImage(false);
    setModalVideo(false);
    setModalDocument(false);
    setImageFile(null);
    setVideoFile(null);
    setDocumentFile(null);
  };

  // ─── Data fetching ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!chat) { setMessages([]); return; }
    setMessages([]);
    setOffset(0);
    setHasMore(true);
    setAssignedName(null);
    setConversationStatus("OPENED");
    setReplyMessage(null);
    closePreviewModal();
    fetchMessages(chat.conversation_id, 0, true);
  }, [chat?.conversation_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setParticipantProfile(null);
    if (!chat?.participant_user_id || !chat?.account_slug) return;
    igGetProfile(chat.account_slug, chat.participant_user_id).then((res) => {
      if (res?.data?.id) setParticipantProfile(res.data);
    });
  }, [chat?.participant_user_id, chat?.account_slug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!chat?.assigned_user_id) { setAssignedName(null); return; }
    serviceGetUsersByUserId(cookies.user, chat.assigned_user_id).then((res) => {
      const user = res?.data?.data?.[0];
      if (user) {
        const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();
        setAssignedName({ name: name || "Agent", theme: user.theme, initial: findUpper(user.firstName || "A") });
      } else {
        setAssignedName({ name: "Agent", theme: "primary", initial: "A" });
      }
    });
  }, [chat?.assigned_user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── WebSocket handlers ────────────────────────────────────────────────────

  useEffect(() => {
    if (!newInboundMessage || !chat) return;
    if (newInboundMessage.chat_id !== chat.conversation_id) return;
    const newMsg = {
      message_id: newInboundMessage.message_id,
      chat_id: newInboundMessage.chat_id,
      from_username: newInboundMessage.from_username,
      from_user_id: newInboundMessage.from_id,
      text: newInboundMessage.text,
      direction: newInboundMessage.direction || "inbound",
      sent_via: newInboundMessage.sent_via || "",
      attachment_type: newInboundMessage.attachment_type || "",
      attachment_url: newInboundMessage.attachment_url || "",
      attachment_file_type: newInboundMessage.attachment_file_type || "",
      attachments: newInboundMessage.attachments || [],
      reply_to_id: newInboundMessage.reply_to_id || "",
      created_time: newInboundMessage.created_time,
      created_at: new Date().toISOString(),
    };
    // Dedup by message_id and replace optimistic placeholders (local_media_*
    // for media, temp_* for text) so an outbound echo never adds a second bubble.
    setMessages((prev) => upsertIncomingMessage(prev, newMsg));
    scrollToBottom();
  }, [newInboundMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!newSystemMessage || !chat) return;
    if (newSystemMessage.chat_id !== chat.conversation_id) return;
    const msg = newSystemMessage.message;
    if (!msg) return;
    setMessages((prev) => {
      if (prev.some((m) => m.message_id === msg.message_id)) return prev;
      return [...prev, { ...msg, direction: "system" }];
    });
    scrollToBottom();
  }, [newSystemMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Quick reply keyboard navigation ──────────────────────────────────────

  useEffect(() => {
    if (showQuickReply && quickReplyData.length && downPress) {
      document.getElementById("ig-chat-textarea")?.blur();
      setCursor((prev) => (prev < quickReplyData.length - 1 ? prev + 1 : prev));
    }
  }, [downPress]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showQuickReply && quickReplyData.length && upPress) {
      document.getElementById("ig-chat-textarea")?.blur();
      setCursor((prev) => (prev > 0 ? prev - 1 : prev));
    }
  }, [upPress]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showQuickReply && quickReplyData.length && (enterPress || tabPress)) {
      quickReplySelect(quickReplyData[cursor]);
    }
  }, [enterPress, tabPress]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showQuickReply && quickReplyData.length && hovered) {
      setCursor(quickReplyData.indexOf(hovered));
    }
  }, [hovered]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Core actions ─────────────────────────────────────────────────────────

  const fetchMessages = async (chatId, currentOffset, replace = false) => {
    if (replace) setIsLoading(true);
    else setIsLoadingMore(true);
    const res = await igGetMessages(chatId, PAGE_SIZE, currentOffset);
    if (res?.data && Array.isArray(res.data)) {
      // API returns newest-first; reverse so the UI renders oldest -> newest
      const fetched = [...res.data].reverse();
      if (replace) { setMessages(fetched); setTimeout(scrollToBottom, 100); }
      else setMessages((prev) => [...fetched, ...prev]);
      setHasMore(fetched.length === PAGE_SIZE);
    }
    if (replace) setIsLoading(false);
    else setIsLoadingMore(false);
  };

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleLoadMore = () => {
    if (!chat || isLoadingMore || !hasMore) return;
    const nextOffset = offset + PAGE_SIZE;
    setOffset(nextOffset);
    fetchMessages(chat.conversation_id, nextOffset);
  };

  const handleSend = async () => {
    if (conversationStatus === "RESOLVED") return;

    const hasMedia = imageFile || videoFile || documentFile;
    const text = inputText.trim();
    if (!text && !hasMedia) return;
    if (!chat || isSending) return;

    setIsSending(true);

    // ── Media path ──────────────────────────────────────────────────────────
    if (hasMedia) {
      let file, mediaType;

      if (imageFile) {
        if (imageFile.size > 10 * 1024 * 1024) {
          Swal.fire({ icon: "error", title: "Picture too large", text: "Ukuran Gambar tidak boleh melebihi 10 MB", focusConfirm: false });
          setIsSending(false);
          return;
        }
        if (!imageFile.type.includes("image")) {
          Swal.fire({ icon: "error", title: "File unsupported", text: "Mohon gunakan gambar dengan format png/jpeg/jpg", focusConfirm: false });
          setIsSending(false);
          return;
        }
        file = imageFile;
        mediaType = "image";
      } else if (documentFile) {
        if (documentFile.size > 10 * 1024 * 1024) {
          Swal.fire({ icon: "error", title: "File too large", text: "Ukuran file tidak boleh melebihi 10 MB", focusConfirm: false });
          setIsSending(false);
          return;
        }
        file = documentFile;
        mediaType = "file";
      } else if (videoFile) {
        if (videoFile.size > 10 * 1024 * 1024) {
          Swal.fire({ icon: "error", title: "Video too large", text: "Ukuran Video tidak boleh melebihi 10 MB", focusConfirm: false });
          setIsSending(false);
          return;
        }
        if (!videoFile.type.includes("video")) {
          Swal.fire({ icon: "error", title: "File unsupported", text: "Mohon gunakan video dengan format mp4, 3gpp", focusConfirm: false });
          setIsSending(false);
          return;
        }
        file = videoFile;
        mediaType = "video";
      }

      closePreviewModal();
      setInputText("");

      const res = await igSendMediaMessage(chat.conversation_id, file, mediaType, text);
      if (!res || res.status < 200 || res.status >= 300) {
        Swal.fire({ icon: "error", title: "Gagal mengirim", text: "Gagal mengirim attachment. Silakan coba lagi.", focusConfirm: false });
      } else {
        // res.data._attachmentUrl diisi oleh igSendMediaMessage (S3 URL permanen)
        setMessages((prev) => [
          ...prev,
          {
            message_id: "local_media_" + Date.now(),
            chat_id: chat.conversation_id,
            from_username: chat.account_slug,
            from_user_id: "",
            text: text,
            direction: "outbound",
            sent_via: "crm",
            attachment_type: mediaType,
            attachment_url: res.data._attachmentUrl || null,
            created_time: new Date().toISOString(),
            created_at: new Date().toISOString(),
          },
        ]);
        scrollToBottom();
      }

      setIsSending(false);
      return;
    }

    // ── Text-only path ──────────────────────────────────────────────────────
    setInputText("");
    setShowQuickReplySync(false);
    setSearchQuickReply("");
    setQuickReplyData([]);
    setCursor(0);

    const replyToId = replyMessage?.message_id || "";
    setReplyMessage(null);

    const tempMsg = {
      message_id: "temp_" + Date.now(),
      chat_id: chat.conversation_id,
      from_username: chat.account_slug,
      from_user_id: "",
      text,
      direction: "outbound",
      sent_via: "crm",
      attachment_type: "",
      attachment_url: "",
      reply_to_id: replyToId,
      created_time: new Date().toISOString(),
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempMsg]);
    scrollToBottom();

    const res = await igSendMessage(chat.conversation_id, text, replyToId);
    if (!res || res.status < 200 || res.status >= 300) {
      setMessages((prev) => prev.filter((m) => m.message_id !== tempMsg.message_id));
      Swal.fire({ icon: "error", title: "Gagal mengirim", text: "Gagal mengirim pesan. Silakan coba lagi.", focusConfirm: false });
    }
    setIsSending(false);
  };

  const setShowQuickReplySync = (val) => {
    showQuickReplyRef.current = val;
    setShowQuickReply(val);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputText(value);
    if (value.includes("/")) {
      setShowQuickReplySync(true);
      const query = value.split("/").filter(Boolean).pop() || "";
      setSearchQuickReply("/" + query);

      if (quickReplyDebounceRef.current) {
        clearTimeout(quickReplyDebounceRef.current);
      }
      setIsQuickReplyLoading(true);
      quickReplyDebounceRef.current = setTimeout(() => {
        fetchQuickReply(query);
      }, 500);
    } else {
      if (quickReplyDebounceRef.current) {
        clearTimeout(quickReplyDebounceRef.current);
      }
      setShowQuickReplySync(false);
      setSearchQuickReply("");
      setQuickReplyData([]);
      setIsQuickReplyLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !showQuickReplyRef.current) {
      e.preventDefault();
      if (!isSending && (inputText.trim() || imageFile || videoFile || documentFile)) {
        handleSend();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData || window.clipboardData;
    if (clipboardData.files && clipboardData.files.length > 0) {
      const file = clipboardData.files[0];
      if (file.type.startsWith("image/")) {
        setImageInput(file);
      } else if (file.type.startsWith("video/")) {
        setVideoInput(file);
      } else {
        Swal.fire({
          icon: "error",
          title: "Format tidak didukung",
          text: "Instagram hanya mendukung pengiriman gambar (jpg/png) dan video (mp4). File dokumen tidak dapat dikirim.",
          focusConfirm: false,
        });
        return;
      }
      setChatOptions(false);
    } else {
      const pastedText = clipboardData.getData("text");
      if (pastedText) {
        const textarea = e.target;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newText = textarea.value.substring(0, start) + pastedText + textarea.value.substring(end);
        setInputText(newText);
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + pastedText.length;
        }, 0);
      }
    }
  };

  const addEmoji = (data) => {
    setInputText((prev) => prev + data.emoji);
    emojiButtonRef?.current?.click();
  };

  const fetchQuickReply = async (query = "") => {
    const deptIds = Array.isArray(cookies.departmentIds)
      ? cookies.departmentIds.filter(Boolean)
      : [cookies.departmentIds].filter(Boolean);

    const res = await serviceGetQuickReplyChat(cookies.SIDikey, query, deptIds);

    if (res?.status === 200 && res?.data) {
      const formatted = res.data.map((item) => ({
        name: item.name,
        content: item.response,
        command: "/" + item.command,
      }));
      setQuickReplyData(formatted);
    } else {
      setQuickReplyData([]);
    }
    setIsQuickReplyLoading(false);
  };

  const quickReplySelect = (item) => {
    const newInput = inputText.replace(searchQuickReply, item.content);
    setInputText(newInput);
    setShowQuickReplySync(false);
    setSearchQuickReply("");
    setQuickReplyData([]);
    setCursor(0);
    document.getElementById("ig-chat-textarea")?.focus();
  };

  const handleAssigned = (userName) => {
    setAssignedName({ name: userName, theme: "primary", initial: findUpper(userName || "A") });
  };

  const handleResolved = () => {
    setConversationStatus("RESOLVED");
  };

  const toggleSearch = () => setSearchSidebar((prev) => !prev);
  const closeSearch = () => setSearchSidebar(false);

  useEffect(() => {
    if (!scrollToMessageId) return;
    const element = document.querySelector(`[id='${scrollToMessageId}']`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      element.classList.add("highlight__animate");
      setTimeout(() => element.classList.remove("highlight__animate"), 4000);
    }
    setScrollToMessageId(null);
  }, [scrollToMessageId]);

  // ─── Render ────────────────────────────────────────────────────────────────

  if (!chat) {
    return (
      <div className="nk-chat-body show-chat">
        <div className="nk-chat-blank">
          <p className="text-muted">Pilih conversation untuk memulai</p>
        </div>
      </div>
    );
  }

  const participantName = chat.participant_username || chat.participant_user_id || "Unknown";
  const isResolved = conversationStatus === "RESOLVED";

  return (
    <div className={classNames("nk-chat-body show-chat", { "profile-shown": searchSidebar && window.innerWidth > 1550 })}>
      {/* Header */}
      <div className="nk-chat-head" ref={bodyChatRef}>
        <ul className="nk-chat-head-info">
          <li className="d-flex align-items-center">
            {participantProfile?.username ? (
              <a
                href={`https://instagram.com/${participantProfile.username}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Buka profil Instagram"
              >
                <UserAvatar image={participantProfile.avatarUrl || avatarPlaceholder} className="chat-media mr-2" />
              </a>
            ) : (
              <UserAvatar image={participantProfile?.avatarUrl || avatarPlaceholder} className="chat-media" />
            )}
            <div className="user-info ms-2">
              {participantProfile?.username ? (
                <a
                  href={`https://instagram.com/${participantProfile.username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="lead-text"
                  style={{ color: "inherit", textDecoration: "none" }}
                  title="Buka profil Instagram"
                >
                  @{participantName}
                  <Icon name="external-link" className="ms-1 text-soft" style={{ fontSize: 11 }} />
                </a>
              ) : (
                <span className="lead-text">@{participantName}</span>
              )}
            </div>
          </li>
        </ul>
        <ul className="nk-chat-head-tools">
          <li className="d-none d-sm-block">
            <a
              href="#search"
              onClick={(e) => { e.preventDefault(); toggleSearch(); }}
              className="btn btn-icon btn-trigger text-primary chat-profile-toggle"
            >
              <Icon name="search" />
            </a>
          </li>
          {roleFunctions.includes("assign") && (
            <li className="d-none d-sm-block">
              <UncontrolledDropdown>
                <DropdownToggle
                  tag="a"
                  className="dropdown-toggle btn btn-icon btn-trigger text-primary"
                  onClick={() => {
                    if (!roleFunctions.includes("assign") || isResolved) return;
                    setIsAssignOpen(true);
                  }}
                >
                  <Icon name="emails" />
                </DropdownToggle>
              </UncontrolledDropdown>
            </li>
          )}
        </ul>
      </div>

      {/* Preview modal — same placement as WA ChatBody (below header, keeps header visible) */}
      <div>
        <div
          className={!modalImage && !modalDocument && !modalVideo ? "d-none" : "send-image"}
          style={{ width: bodyChatRef.current ? bodyChatRef.current.offsetWidth : "auto" }}
        >
          <div className="send-image-content">
            <a
              href="#cancel"
              onClick={(ev) => { ev.preventDefault(); closePreviewModal(); }}
              className="send-image-close text-soft"
            >
              <Icon name="cross-sm" />
            </a>

            {/* Image preview */}
            <section style={{ height: "75%" }} className={!modalImage ? "d-none" : ""}>
              <div className="d-flex justify-content-center p-4 h-100">
                <img
                  alt="preview"
                  className="rounded"
                  style={{ height: "100%", objectFit: "contain" }}
                  src={imageFile ? URL.createObjectURL(imageFile) : ""}
                />
              </div>
            </section>

            {/* Document preview */}
            <section style={{ height: "75%" }} className={!modalDocument ? "d-none" : ""}>
              <div className="d-flex justify-content-center p-4 h-100">
                <div className="mt-5 text-center">
                  <Icon name="file-fill" className="file-thumbnail" />
                  <div className="mt-2 file-name">{documentFile ? documentFile.name : ""}</div>
                  <div className="file-info">
                    {documentFile
                      ? convertFileSize(documentFile.size) + " - " + documentFile.type.toUpperCase()
                      : ""}
                  </div>
                </div>
              </div>
            </section>

            {/* Video preview */}
            <section style={{ height: "70%" }} className={!modalVideo ? "d-none" : ""}>
              <div className="d-flex justify-content-center p-4 h-100">
                <video preload="auto" controls style={{ height: "100%" }}>
                  <source src={videoFile ? URL.createObjectURL(videoFile) : ""} />
                </video>
              </div>
            </section>

            {/* Caption + send button inside preview modal */}
            <div className="border-top d-flex align-items-center">
              <div className="nk-chat-editor w-100">
                <div className="nk-chat-editor-form">
                  <div className="form-control-wrap">
                    <textarea
                      id="ig-chat-textarea-media"
                      ref={inputChatMedia}
                      style={modalDocument ? { backgroundColor: "white" } : { whiteSpace: "pre-line" }}
                      className="form-control form-control-simple no-resize"
                      rows={inputText.split("\n").length > 5 ? 5 : inputText.split("\n").length || 1}
                      onChange={modalDocument ? null : (e) => setInputText(e.target.value)}
                      value={modalDocument ? "" : inputText}
                      placeholder={modalDocument ? "" : "Type your message..."}
                      onKeyDown={handleKeyDown}
                      onPaste={handlePaste}
                      disabled={modalDocument ? true : false}
                    />
                  </div>
                </div>
                <ul className="nk-chat-editor-tools g-2">
                  <li>
                    <Button
                      color="primary"
                      onClick={handleSend}
                      className="btn-round btn-icon"
                      disabled={isSending}
                    >
                      {isSending ? <Spinner size="sm" /> : <Icon size="xl" name="send-alt" />}
                    </Button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <SimpleBar className="nk-chat-panel">
        {hasMore && (
          <div className="text-center py-2">
            {isLoadingMore ? (
              <Spinner size="sm" />
            ) : (
              <button className="btn btn-xs btn-light" onClick={handleLoadMore}>
                Load older messages
              </button>
            )}
          </div>
        )}
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner />
          </div>
        ) : (
          <div className="chat-msgs">
            {buildItems(messages).map((item) =>
              item.type === "separator" ? (
                <ChatDateSeparator key={item.key} label={item.label} />
              ) : (
                <IgChatBubble
                  key={item.key}
                  item={item.msg}
                  participantUsername={participantName}
                  participantAvatarUrl={participantProfile?.avatarUrl}
                  repliedMessage={item.msg.reply_to_id ? messagesById[item.msg.reply_to_id] : undefined}
                  onReply={isResolved ? undefined : setReplyMessage}
                />
              ),
            )}
            <div ref={bottomRef} />
          </div>
        )}
      </SimpleBar>

      {/* Quick Reply Dropdown */}
      {showQuickReply && (
        <div className="quick-reply-content">
          {isQuickReplyLoading ? (
            <div className="d-flex justify-content-center align-items-center p-3">
              <Spinner size="sm" color="primary" />
            </div>
          ) : quickReplyData.length > 0 ? (
            quickReplyData.map((item, idx) => (
              <QuickReplyItem
                key={idx}
                active={cursor === idx}
                command={item.command}
                content={item.content}
                clickEvent={() => quickReplySelect(item)}
                mouseEnterEvent={() => setHovered(item)}
                mouseLeaveEvent={() => setHovered(undefined)}
              />
            ))
          ) : (
            <p className="m-2 text-muted" style={{ fontSize: 13 }}>
              No quick reply data found!
            </p>
          )}
        </div>
      )}

      {/* Reply preview above input (same structure as WA ChatBody) */}
      {replyMessage && !isResolved ? (
        <div className="input-reply border-bottom">
          <div className="input-reply-content">
            <div className="input-reply-title">
              {replyMessage.direction === "outbound" ? (
                <span>Anda : </span>
              ) : (
                <span>{participantName || replyMessage.from_username} : </span>
              )}
            </div>
            <div className="">{replyMessage.text || replyMessage.attachment_type || "Message"}</div>
          </div>
          <ul className="input-reply-close">
            <Button size="lg" onClick={() => setReplyMessage(null)}>
              <Icon name="cross" />
            </Button>
          </ul>
        </div>
      ) : null}

      {/* Chat input */}
      {!isResolved ? (
        <div className="nk-chat-editor">
          <div className="nk-chat-editor-upload ml-n1">
            {/* Attachment options (same structure as WA ChatBody) */}
            <div className={`chat-upload-option ${chatOptions ? "expanded" : ""}`}>
              <ul className="">
                <li>
                  <a href="#img" onClick={(ev) => ev.preventDefault()}>
                    <Icon name="img-fill" onClick={handleUploadImageClick} />
                  </a>
                  <input id="ig-select-image" hidden type="file" accept="image/*" onChange={handleImageFileChange} />
                </li>
                {/* File/dokumen tidak didukung oleh Instagram API — tombol disembunyikan */}
                <li>
                  <a href="#video" onClick={(ev) => ev.preventDefault()}>
                    <Icon name="video-fill" onClick={handleUploadVideoClick} />
                  </a>
                  <input id="ig-select-video" hidden type="file" accept="video/*" onChange={handleVideoFileChange} />
                </li>
              </ul>
            </div>
            <Button
              size="sm"
              className={`btn-icon btn-trigger text-primary toggle-opt ${chatOptions ? "active" : ""}`}
              onClick={onChatOptions}
            >
              <Icon name="plus-circle-fill" />
            </Button>

            <Button
              size="sm"
              className={classNames(showEmojiPicker ? "d-flex" : "d-none", "btn-icon btn-trigger text-primary")}
              onClick={() => setShowEmojiPicker(false)}
            >
              <Icon name="cross" />
            </Button>
            <Button
              ref={emojiButtonRef}
              className="btn-icon btn-trigger text-primary toggle-opt"
              onClick={() => setShowEmojiPicker((prev) => !prev)}
            >
              <Icon name="happyf-fill" />
            </Button>
            {showEmojiPicker && (
              <div className="emoji-picker">
                <EmojiPicker emojiStyle="google" onEmojiClick={addEmoji} />
              </div>
            )}
          </div>

          <div className={`nk-chat-editor-form ${chatOptions ? "ml-5rem" : ""}`}
            style={{ marginLeft: chatOptions ? "120px" : "" }}
          >
            <textarea
              id="ig-chat-textarea"
              className="form-control form-control-simple"
              rows={inputText.split("\n").length > 10 ? 10 : inputText.split("\n").length || 1}
              placeholder="Type your messages..."
              value={inputText}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              style={{ resize: "none", whiteSpace: "pre-line" }}
            />
          </div>

          <ul className="nk-chat-editor-tools g-2">
            <li>
              <Button
                className="btn-round btn-icon"
                color="primary"
                onClick={handleSend}
                disabled={(!inputText.trim() && !imageFile && !videoFile && !documentFile) || isSending}
              >
                {isSending ? <Spinner size="sm" /> : <Icon size="xl" name="send-alt" />}
              </Button>
            </li>
          </ul>
        </div>
      ) : (
        <div className="nk-chat-editor disabled-chat">
          <div className="nk-chat-editor-form">
            <textarea
              className="form-control form-control-simple"
              rows={1}
              placeholder="Conversation ini sudah di-resolve"
              disabled
              style={{ resize: "none" }}
            />
          </div>
        </div>
      )}

      {/* Modal Resolve */}
      <IgChatModalResolve
        isOpen={isResolveOpen}
        setIsOpen={setIsResolveOpen}
        chat={chat}
        conversationStatus={conversationStatus}
        onResolved={handleResolved}
        setMessages={setMessages}
        roleFunctions={roleFunctions}
      />

      {/* Modal Assign */}
      <IgChatModalAssign
        isOpen={isAssignOpen}
        onClose={() => setIsAssignOpen(false)}
        chatId={chat.conversation_id}
        onAssigned={handleAssigned}
      />

      {/* Search Sidebar */}
      <IgChatSearchSidebar
        searchSidebar={searchSidebar}
        messages={messages}
        onSelectMessage={(messageId) => setScrollToMessageId(messageId)}
        close={closeSearch}
      />
    </div>
  );
};

export default IgChatBody;
